import React, { useState, useEffect } from 'react';
import { Search, MapPin, FileText, User, Home, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { getGoogleMapsUrl } from '@/lib/mapUtils';
import AddressWithMap from '@/components/AddressWithMap';

const PesquisaDashboard = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState({
    requisitantes: [],
    processos: [],
    imoveis: []
  });
  const [hasSearched, setHasSearched] = useState(false);
  const navigate = useNavigate();

  // Load data once
  const [data, setData] = useState({ requisitantes: [], processos: [], imoveis: [] });

  useEffect(() => {
    setData({
        requisitantes: JSON.parse(localStorage.getItem('requisitantes') || '[]'),
        processos: JSON.parse(localStorage.getItem('processos') || '[]'),
        imoveis: JSON.parse(localStorage.getItem('imoveis') || '[]'),
    });
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    const term = searchTerm.toLowerCase();
    
    const reqs = data.requisitantes.filter(r => 
        r.nome.toLowerCase().includes(term) || 
        r.documento.includes(term) ||
        (r.email && r.email.toLowerCase().includes(term))
    );

    const procs = data.processos.filter(p => 
        p.numero.includes(term) || 
        p.tipoLaudo.toLowerCase().includes(term)
    );

    const imovs = data.imoveis.filter(i => 
        i.endereco.toLowerCase().includes(term) || 
        (i.descricao && i.descricao.toLowerCase().includes(term)) ||
        (i.bairro && i.bairro.toLowerCase().includes(term))
    );

    setResults({ requisitantes: reqs, processos: procs, imoveis: imovs });
    setHasSearched(true);
  };

  return (
    <div className="container mx-auto px-4 max-w-5xl py-8 animate-in fade-in">
        <div className="text-center mb-10">
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Busca Unificada</h1>
            <p className="text-slate-500">Pesquise por processos, requisitantes ou imóveis em todo o sistema.</p>
        </div>

        <Card className="mb-10 shadow-lg border-indigo-100">
            <CardContent className="p-6">
                <form onSubmit={handleSearch} className="flex gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                        <Input 
                            className="pl-10 h-11 text-lg" 
                            placeholder="Digite nome, CPF, número do processo ou endereço..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <Button type="submit" size="lg" className="bg-indigo-600 hover:bg-indigo-700 h-11 px-8">
                        Pesquisar
                    </Button>
                </form>
            </CardContent>
        </Card>

        {hasSearched && (
            <div className="space-y-8">
                {/* Requisitantes */}
                {results.requisitantes.length > 0 && (
                    <section>
                        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                            <User className="text-indigo-600" /> Requisitantes Encontrados
                        </h2>
                        <div className="grid gap-4 md:grid-cols-2">
                            {results.requisitantes.map(r => (
                                <Card key={r.id} className="hover:shadow-md transition-shadow">
                                    <CardContent className="p-4 flex justify-between items-center">
                                        <div>
                                            <div className="font-bold">{r.nome}</div>
                                            <div className="text-sm text-slate-500">{r.documento}</div>
                                            <div className="text-xs text-slate-400 mt-1">{r.email}</div>
                                        </div>
                                        <Button variant="ghost" onClick={() => navigate('/requisitantes')}>Ver <ArrowRight className="ml-1 w-4 h-4"/></Button>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </section>
                )}

                {/* Processos */}
                {results.processos.length > 0 && (
                     <section>
                        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                            <FileText className="text-indigo-600" /> Processos Encontrados
                        </h2>
                        <div className="grid gap-4 md:grid-cols-2">
                            {results.processos.map(p => (
                                <Card key={p.id} className="hover:shadow-md transition-shadow">
                                    <CardContent className="p-4 flex justify-between items-center">
                                        <div>
                                            <div className="font-mono font-bold text-indigo-700">{p.numero}</div>
                                            <div className="text-sm text-slate-600">{p.tipoLaudo}</div>
                                            <Badge variant="outline" className="mt-1">{p.status}</Badge>
                                        </div>
                                        <Button variant="ghost" onClick={() => navigate('/requisitantes')}>Ver <ArrowRight className="ml-1 w-4 h-4"/></Button>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </section>
                )}

                {/* Imóveis */}
                {results.imoveis.length > 0 && (
                     <section>
                        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                            <Home className="text-indigo-600" /> Imóveis Encontrados
                        </h2>
                        <div className="grid gap-4 md:grid-cols-2">
                            {results.imoveis.map(i => (
                                <Card key={i.id} className="hover:shadow-md transition-shadow">
                                    <CardContent className="p-4">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <AddressWithMap 
                                                    text={`${i.endereco}, ${i.numero}`} 
                                                    data={i} 
                                                    className="font-bold text-base"
                                                />
                                                <div className="text-sm text-slate-500">{i.bairro} - {i.cidade}</div>
                                                {i.descricao && <div className="text-xs text-slate-400 mt-1 italic">{i.descricao}</div>}
                                            </div>
                                        </div>
                                        <div className="mt-3 flex justify-end">
                                             <Button variant="ghost" size="sm" onClick={() => navigate('/imoveis')}>Detalhes <ArrowRight className="ml-1 w-4 h-4"/></Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </section>
                )}

                {results.requisitantes.length === 0 && results.processos.length === 0 && results.imoveis.length === 0 && (
                    <div className="text-center py-12 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                        <div className="text-slate-400 mb-2">Nenhum resultado encontrado para "{searchTerm}"</div>
                        <p className="text-sm text-slate-400">Tente buscar por outras palavras-chave.</p>
                    </div>
                )}
            </div>
        )}
    </div>
  );
};

export default PesquisaDashboard;